import * as React from 'react'

import { classList } from 'react-classlist-helper'

import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import AvatarType from '~/client/src/shared/enums/AvatarType'

import { FormFieldType } from '../../enums/FormFieldType'
import { TagIconType } from '../../enums/TagIcon'
import { TagType } from '../../enums/TagType'
import { Field } from '../../types/CustomFieldType'
import {
  MAX_AVATAR_HEIGHT,
  MAX_AVATAR_WIDTH,
  TWO_MB,
} from '../../utils/fileValidationHelper'
import { EMPTY_STRING } from '../../utils/usefulStrings'
import AvatarInput from '../AvatarInput/AvatarInput'
import ImageInput from '../ImageInput/ImageInput'
import StruxhubInputLabel from '../StruxhubInputs/components/StruxhubInputLabel'
import StruxhubIncrementalSearch from '../StruxhubInputs/StruxhubIncrementalSearch/StruxhubIncrementalSearch'
import StruxhubInput from '../StruxhubInputs/StruxhubInput'
import StruxhubPhoneInput from '../StruxhubInputs/StruxhubPhoneInput'
import StruxhubSelect from '../StruxhubInputs/StruxhubSelect'
import StruxhubTagAsTextInput from '../StruxhubInputs/StruxhubTagAsTextInput'
import StruxhubTagInput from '../StruxhubInputs/StruxhubTagInput'
import StruxhubTagsInput from '../StruxhubInputs/StruxhubTagsInput/StruxhubTagsInput'

import './FormFieldWrapper.scss'

interface IProps {
  field: Field
  avatarType?: AvatarType
  className?: string
}

export default class FormFieldWrapper extends React.Component<IProps> {
  private static renderInput(
    {
      type,
      options,
      onChange,
      onValueReset,
      id,
      value,
      minlength,
      maxlength,
      minNumber,
      maxNumber,
      serviceInfo,
      isItemCreationAllowed,
      isRequired,
      isValid,
      isLoading,
      pattern,
      inline,
      isReadonly,
      isChanged,
      restrictedValues,
      label,
      additionalHelperText,
      validationMessage,
      negativeOrDecimal,
      previewMember,
      membersList,
      maxResults,
      onCancel,
      onClear,
      onApply,
    }: Field,
    avatarType?: AvatarType,
  ) {
    switch (type) {
      case FormFieldType.Tag:
        return (
          <StruxhubTagInput
            label={label}
            isValid={isValid}
            isRequired={isRequired}
            isReadOnly={isReadonly}
            hasShortWidth={!!inline}
            onChange={onChange}
            tagType={serviceInfo as TagType}
            value={value}
            isTagCreationAllowed={isItemCreationAllowed}
            restrictedValues={restrictedValues}
          />
        )

      case FormFieldType.Tags:
        return (
          <StruxhubTagsInput
            label={label}
            isValid={isValid}
            isRequired={isRequired}
            isReadOnly={isReadonly}
            hasShortWidth={!!inline}
            onChange={onChange}
            tagType={serviceInfo as TagType}
            values={value}
            isTagCreationAllowed={isItemCreationAllowed}
            restrictedValues={restrictedValues}
          />
        )

      case FormFieldType.TextAsTag:
        return (
          <StruxhubTagAsTextInput
            label={label}
            iconName={serviceInfo as TagIconType}
            value={value || EMPTY_STRING}
            isRequired={isRequired}
            isReadOnly={isReadonly}
            isValid={isValid}
            hasShortWidth={!!inline}
            onChange={onChange}
            onValueReset={onValueReset}
          />
        )

      case FormFieldType.SelectOne:
        return (
          <StruxhubSelect
            name={id}
            label={label}
            isValid={isValid}
            isRequired={isRequired}
            isReadOnly={isReadonly}
            hasShortWidth={!!inline}
            value={value}
            onChange={onChange}
          >
            {(options || []).map(o => (
              <option key={o.value} value={o.value} disabled={o.disabled}>
                {o.name}
              </option>
            ))}
          </StruxhubSelect>
        )

      case FormFieldType.Phone:
        return (
          <StruxhubPhoneInput
            label={label}
            value={value || EMPTY_STRING}
            isRequired={isRequired}
            isValid={isValid}
            isReadOnly={isReadonly}
            hasShortWidth={!!inline}
            onChange={onChange}
          />
        )

      case FormFieldType.Avatar:
        return (
          <AvatarInput
            data={value}
            onChange={onChange}
            avatarType={avatarType}
            maxFileSize={TWO_MB}
            maxWidth={MAX_AVATAR_WIDTH}
            maxHeight={MAX_AVATAR_HEIGHT}
          />
        )

      case FormFieldType.Switch:
        return (
          <>
            {!inline && (
              <StruxhubInputLabel
                label={label}
                value={value}
                isRequired={isRequired}
                isValid={isValid}
              />
            )}
            <label className="tw:mt-2.5 tw:flex tw:items-center tw:gap-2.5">
              <Switch
                checked={!!value}
                onCheckedChange={checked =>
                  onChange({
                    currentTarget: {
                      name: id,
                      checked,
                      type: FormFieldType.Switch,
                    },
                    target: { name: id, checked, type: FormFieldType.Switch },
                  })
                }
                name={id}
                disabled={isReadonly}
              />
              {!!inline && <span>{label}</span>}
            </label>
          </>
        )
      case FormFieldType.Image:
        return <ImageInput value={value} label={label} onChange={onChange} />

      case FormFieldType.IncrementalSearch:
        return (
          <StruxhubIncrementalSearch
            label={label}
            additionalHelperText={additionalHelperText}
            validationMessage={validationMessage}
            isValid={isValid}
            isChanged={isChanged}
            value={value || []}
            pattern={pattern || EMPTY_STRING}
            isRequired={isRequired}
            isLoading={isLoading}
            onChange={onChange}
            minLength={minlength}
            maxResults={maxResults}
            membersList={membersList}
            maxNumber={maxNumber}
            onClear={onClear}
            onApply={onApply}
            onCancel={onCancel}
            previewMember={previewMember}
          />
        )

      default:
        return (
          <StruxhubInput
            label={label}
            name={id}
            type={type}
            value={value || EMPTY_STRING}
            isRequired={isRequired}
            isReadOnly={isReadonly}
            isValid={isValid}
            hasShortWidth={!!inline}
            onChange={onChange}
            onValueReset={onValueReset}
            min={minNumber}
            max={maxNumber}
            minLength={minlength}
            maxLength={maxlength}
            negativeOrDecimal={negativeOrDecimal}
            pattern={pattern}
            validationMessage={validationMessage}
          />
        )
    }
  }

  public render() {
    const { field, avatarType, className } = this.props
    const { inline, isHidden } = field

    return (
      <div
        className={classList({
          [className]: !!className,
          'col px8 form-field-wrapper-input': true,
          [`inline-${inline} vertical-align-middle inline-flex`]: !!inline,
          hidden: isHidden,
        })}
      >
        {FormFieldWrapper.renderInput(field, avatarType)}
      </div>
    )
  }
}
